import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LuCheck, LuInfo } from 'react-icons/lu';

import { Tooltip } from 'antd';
import ShortcutHint from 'components/common/ShortcutHint/ShortcutHint';
import {
  CASHFLOW_DETAIL_PRODUCTS,
  CASHFLOW_NO_DATA_MESSAGE,
} from 'constants/uiConstants';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import WorkspaceEmptyState from 'pages/FarmsPage/Workspace/WorkspaceEmptyState';
import { formatCurrency } from 'utils/CurrencyUtils';

import type { CashflowDetailLevel } from '../../../../../redux/slices/InsightsSlice';

import type { CashflowLine, CashflowResult } from './computeCashflow';

interface CashflowGridProps {
  data: CashflowResult;
  detailLevel: CashflowDetailLevel;
  /**
   * Reports whenever the active range selection changes, so a "master" copy
   * control elsewhere (e.g. CashflowView's own Copy to clipboard button)
   * can switch to copying just the selection instead of the whole grid —
   * otherwise that button would silently do the wrong thing (copy
   * everything) right when a user has a selection active and would
   * reasonably expect it to act on that selection instead.
   */
  onSelectionChange?: (
    hasSelection: boolean,
    getSelectionText: () => string
  ) => void;
}

/**
 * One addressable cell — `text` is the plain value used for copy and for
 * Sum/Average/Count math (via `numericValue`); `display` overrides what's
 * shown on screen only (e.g. Cash Flow's blank Total column still copies as
 * '' but displays a dashed "—" with a tooltip).
 */
interface GridCell {
  text: string;
  display?: string;
  numericValue?: number;
  tooltip?: string;
}

interface LineRowModel {
  key: string;
  rowIndex: number;
  cells: GridCell[];
  indent?: boolean;
  bold?: boolean;
  negativeIsDanger?: boolean;
}

interface GridModel {
  headerRowIndex: number;
  incomeLabelRowIndex: number;
  incomeLines: LineRowModel[];
  totalIncome: LineRowModel;
  expenditureLabelRowIndex: number;
  expenditureLines: LineRowModel[];
  totalExpenditure: LineRowModel;
  balance: LineRowModel;
  cashFlow: LineRowModel;
  /** Flat lookup by rowIndex, for selection math/copy — includes header + blank label rows too. */
  cellsByRow: Map<number, GridCell[]>;
}

const lineToCells = (
  line: CashflowLine,
  options?: { blankTotal?: boolean }
): GridCell[] => {
  const cells: GridCell[] = [{ text: line.label }];
  if (options?.blankTotal) {
    cells.push({
      text: '',
      display: '—',
      tooltip:
        "A running balance isn't summed. See the No Date/period columns instead.",
    });
  } else {
    cells.push({
      text: formatCurrency(line.values[0]),
      numericValue: line.values[0],
    });
  }
  for (let i = 1; i < line.values.length; i++) {
    cells.push({
      text: formatCurrency(line.values[i]),
      numericValue: line.values[i],
    });
  }
  return cells;
};

const blankRowCells = (label: string, columnCount: number): GridCell[] => {
  const cells: GridCell[] = [{ text: label }];
  for (let i = 1; i < columnCount; i++) cells.push({ text: '' });
  return cells;
};

/**
 * Assigns a stable rowIndex to every visual row (header, body, footer) in
 * display order, and builds each row's cell values — the single source of
 * truth both the table and the selection/copy/Sum-Average-Count logic read
 * from, so they can never drift out of sync with what's on screen.
 */
const buildGridModel = (
  data: CashflowResult,
  showProducts: boolean
): GridModel => {
  const columnCount = 3 + data.periods.length;
  const cellsByRow = new Map<number, GridCell[]>();
  let rowIndex = 0;

  const headerRowIndex = rowIndex++;
  cellsByRow.set(headerRowIndex, [
    { text: 'Line item' },
    { text: 'Total' },
    { text: 'No Date' },
    ...data.periods.map((p) => ({ text: p.label })),
  ]);

  const incomeLabelRowIndex = rowIndex++;
  cellsByRow.set(incomeLabelRowIndex, blankRowCells('Income', columnCount));

  const incomeLines: LineRowModel[] = showProducts
    ? data.incomeLines.map((line) => {
        const cells = lineToCells(line);
        const r = rowIndex++;
        cellsByRow.set(r, cells);
        return { key: `income-${line.key}`, rowIndex: r, cells, indent: true };
      })
    : [];

  const totalIncomeCells = lineToCells(data.totalIncome);
  const totalIncomeRowIndex = rowIndex++;
  cellsByRow.set(totalIncomeRowIndex, totalIncomeCells);
  const totalIncome: LineRowModel = {
    key: 'totalIncome',
    rowIndex: totalIncomeRowIndex,
    cells: totalIncomeCells,
    bold: true,
  };

  const expenditureLabelRowIndex = rowIndex++;
  cellsByRow.set(
    expenditureLabelRowIndex,
    blankRowCells('Expenditure', columnCount)
  );

  const expenditureLines: LineRowModel[] = [];
  if (showProducts) {
    for (const group of data.expenditureCategories) {
      for (const product of group.products) {
        const cells = lineToCells(product);
        const r = rowIndex++;
        cellsByRow.set(r, cells);
        expenditureLines.push({
          key: `expenditure-${group.category}-${product.key}`,
          rowIndex: r,
          cells,
          indent: true,
        });
      }
    }
  } else {
    for (const group of data.expenditureCategories) {
      const cells = lineToCells(group.total);
      const r = rowIndex++;
      cellsByRow.set(r, cells);
      expenditureLines.push({
        key: `expenditure-${group.category}`,
        rowIndex: r,
        cells,
        bold: true,
      });
    }
  }

  const totalExpenditureCells = lineToCells(data.totalExpenditure);
  const totalExpenditureRowIndex = rowIndex++;
  cellsByRow.set(totalExpenditureRowIndex, totalExpenditureCells);
  const totalExpenditure: LineRowModel = {
    key: 'totalExpenditure',
    rowIndex: totalExpenditureRowIndex,
    cells: totalExpenditureCells,
    bold: true,
  };

  const balanceCells = lineToCells(data.balance);
  const balanceRowIndex = rowIndex++;
  cellsByRow.set(balanceRowIndex, balanceCells);
  const balance: LineRowModel = {
    key: 'balance',
    rowIndex: balanceRowIndex,
    cells: balanceCells,
    bold: true,
    negativeIsDanger: true,
  };

  const cashFlowCells = lineToCells(data.cashFlow, { blankTotal: true });
  const cashFlowRowIndex = rowIndex++;
  cellsByRow.set(cashFlowRowIndex, cashFlowCells);
  const cashFlow: LineRowModel = {
    key: 'cashFlow',
    rowIndex: cashFlowRowIndex,
    cells: cashFlowCells,
    bold: true,
    negativeIsDanger: true,
  };

  return {
    headerRowIndex,
    incomeLabelRowIndex,
    incomeLines,
    totalIncome,
    expenditureLabelRowIndex,
    expenditureLines,
    totalExpenditure,
    balance,
    cashFlow,
    cellsByRow,
  };
};

interface SelectionRange {
  anchorRow: number;
  anchorCol: number;
  focusRow: number;
  focusCol: number;
}

// Every combination of which edges of the selected rectangle a given cell
// sits on ('t'op/'b'ottom/'l'eft/'r'ight, e.g. a single selected cell is
// 'tblr', a cell on just the top edge is 't'), written out as complete
// static Tailwind arbitrary-value classes so the JIT compiler can actually
// find and generate them (see getSelectionShadowClass's own comment).
const SELECTION_SHADOW_CLASSES: Record<string, string> = {
  t: 'shadow-[inset_0_2px_0_0_var(--color-success)]',
  b: 'shadow-[inset_0_-2px_0_0_var(--color-success)]',
  l: 'shadow-[inset_2px_0_0_0_var(--color-success)]',
  r: 'shadow-[inset_-2px_0_0_0_var(--color-success)]',
  tb: 'shadow-[inset_0_2px_0_0_var(--color-success),inset_0_-2px_0_0_var(--color-success)]',
  tl: 'shadow-[inset_0_2px_0_0_var(--color-success),inset_2px_0_0_0_var(--color-success)]',
  tr: 'shadow-[inset_0_2px_0_0_var(--color-success),inset_-2px_0_0_0_var(--color-success)]',
  bl: 'shadow-[inset_0_-2px_0_0_var(--color-success),inset_2px_0_0_0_var(--color-success)]',
  br: 'shadow-[inset_0_-2px_0_0_var(--color-success),inset_-2px_0_0_0_var(--color-success)]',
  lr: 'shadow-[inset_2px_0_0_0_var(--color-success),inset_-2px_0_0_0_var(--color-success)]',
  tbl: 'shadow-[inset_0_2px_0_0_var(--color-success),inset_0_-2px_0_0_var(--color-success),inset_2px_0_0_0_var(--color-success)]',
  tbr: 'shadow-[inset_0_2px_0_0_var(--color-success),inset_0_-2px_0_0_var(--color-success),inset_-2px_0_0_0_var(--color-success)]',
  tlr: 'shadow-[inset_0_2px_0_0_var(--color-success),inset_2px_0_0_0_var(--color-success),inset_-2px_0_0_0_var(--color-success)]',
  blr: 'shadow-[inset_0_-2px_0_0_var(--color-success),inset_2px_0_0_0_var(--color-success),inset_-2px_0_0_0_var(--color-success)]',
  tblr: 'shadow-[inset_0_2px_0_0_var(--color-success),inset_0_-2px_0_0_var(--color-success),inset_2px_0_0_0_var(--color-success),inset_-2px_0_0_0_var(--color-success)]',
};

const STICKY_COL0 = 'sticky left-0 bg-white group-hover:bg-rowHover';
const HEADER_BASE =
  'pb-2 text-sm font-bold text-charcoal sticky top-0 bg-white border-b-2 border-gray-argent';
const FOOTER_BASE =
  'py-1.5 font-bold tabular-nums sticky bottom-0 bg-white border-t-2 border-gray-argent';
const ROW_DIVIDER = 'border-b border-gray-smoke';

const headerCellClass = `${HEADER_BASE} z-10 w-28 px-2 text-right`;
const headerLabelClass = `${HEADER_BASE} z-20 left-0 pl-2 pr-6 text-left ${STICKY_COL0}`;
const bodyCellClass = `w-28 px-2 py-1.5 text-right tabular-nums text-charcoal ${ROW_DIVIDER}`;

const sectionLabelClass =
  'py-2 pl-2 pr-6 text-sm font-bold uppercase tracking-wide text-gray-muted';

/** Small inline info icon for a header — explains a column on the spot, matching AnalysisGrid's HeaderInfo. */
const HeaderInfo: React.FC<{ tooltip: string }> = ({ tooltip }) => (
  <Tooltip title={tooltip}>
    <span className="ml-1 inline-flex cursor-help align-middle">
      <LuInfo size={13} className="text-gray-dark" />
    </span>
  </Tooltip>
);

const NO_DATE_TOOLTIP =
  'Costs or income with no specific date. Typically only found in imported historical data.';

const CashflowGrid: React.FC<CashflowGridProps> = ({
  data,
  detailLevel,
  onSelectionChange,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const handleScroll = () =>
    setIsScrolled((scrollRef.current?.scrollLeft ?? 0) > 0);
  const stickyEdgeClass = isScrolled ? 'border-r border-gray-argent' : '';

  const showProducts = detailLevel === CASHFLOW_DETAIL_PRODUCTS;
  const { periods } = data;

  const hasData =
    data.incomeLines.length > 0 || data.expenditureCategories.length > 0;

  const model = useMemo(
    () => buildGridModel(data, showProducts),
    [data, showProducts]
  );

  // ── Range selection — click a cell to start, drag to extend, matching
  // spreadsheet selection. Read-only: no editing/formulas, just
  // select/copy/see-a-quick-total (Sum/Average/Count) over the selection. ──
  const [selection, setSelection] = useState<SelectionRange | null>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    const handleMouseUp = () => {
      isDraggingRef.current = false;
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const handleCellMouseDown = useCallback(
    (rowIndex: number, colIndex: number) => (e: React.MouseEvent) => {
      e.preventDefault(); // suppress the browser's own text-selection drag
      isDraggingRef.current = true;
      setSelection({
        anchorRow: rowIndex,
        anchorCol: colIndex,
        focusRow: rowIndex,
        focusCol: colIndex,
      });
    },
    []
  );

  const handleCellMouseEnter = useCallback(
    (rowIndex: number, colIndex: number) => () => {
      if (!isDraggingRef.current) return;
      setSelection((prev) =>
        prev ? { ...prev, focusRow: rowIndex, focusCol: colIndex } : prev
      );
    },
    []
  );

  const bounds = useMemo(() => {
    if (!selection) return null;
    return {
      minRow: Math.min(selection.anchorRow, selection.focusRow),
      maxRow: Math.max(selection.anchorRow, selection.focusRow),
      minCol: Math.min(selection.anchorCol, selection.focusCol),
      maxCol: Math.max(selection.anchorCol, selection.focusCol),
    };
  }, [selection]);

  // Selection is shown as a perimeter around the selected rectangle
  // (matching this app's own input-field focus treatment — a 2px success-
  // green line, no fill), using an *inset box-shadow* rather than an actual
  // border. Two reasons: (1) box-shadow never affects box size, so a
  // selected cell can never grow the way an added border would; (2) actual
  // per-cell borders leave a hairline gap at junctions in this table's
  // border-separate model (needed for the sticky header/column to work —
  // border-collapse isn't an option here), which inset shadows don't.
  //
  // Every combination of edges is pre-declared below as a complete, static
  // class string — Tailwind's JIT compiler only generates CSS for class
  // names it can find as a whole literal while scanning the source, so
  // building `shadow-[...]` dynamically via string interpolation at
  // runtime (e.g. `shadow-[${parts.join(',')}]`) silently produces no CSS
  // at all, since that exact combined string never appears literally
  // anywhere in the file.
  const getSelectionShadowClass = useCallback(
    (rowIndex: number, colIndex: number): string => {
      if (!bounds) return '';
      if (
        rowIndex < bounds.minRow ||
        rowIndex > bounds.maxRow ||
        colIndex < bounds.minCol ||
        colIndex > bounds.maxCol
      ) {
        return '';
      }
      const key =
        (rowIndex === bounds.minRow ? 't' : '') +
        (rowIndex === bounds.maxRow ? 'b' : '') +
        (colIndex === bounds.minCol ? 'l' : '') +
        (colIndex === bounds.maxCol ? 'r' : '');
      return SELECTION_SHADOW_CLASSES[key] ?? '';
    },
    [bounds]
  );

  const selectionStats = useMemo(() => {
    if (!bounds) return null;
    const spanned =
      (bounds.maxRow - bounds.minRow + 1) * (bounds.maxCol - bounds.minCol + 1);
    if (spanned < 2) return null; // a single click isn't worth a summary bar
    let sum = 0;
    let count = 0;
    for (let r = bounds.minRow; r <= bounds.maxRow; r++) {
      const cells = model.cellsByRow.get(r);
      if (!cells) continue;
      for (let c = bounds.minCol; c <= bounds.maxCol; c++) {
        const value = cells[c]?.numericValue;
        if (value !== undefined) {
          sum += value;
          count++;
        }
      }
    }
    if (count === 0) return null;
    return { sum, average: sum / count };
  }, [bounds, model]);

  const buildSelectionTsv = useCallback(() => {
    if (!bounds) return '';
    const lines: string[] = [];
    for (let r = bounds.minRow; r <= bounds.maxRow; r++) {
      const cells = model.cellsByRow.get(r);
      if (!cells) continue;
      const cols: string[] = [];
      for (let c = bounds.minCol; c <= bounds.maxCol; c++) {
        cols.push(cells[c]?.text ?? '');
      }
      lines.push(cols.join('\t'));
    }
    return lines.join('\n');
  }, [bounds, model]);

  const { copied: selectionCopied, copy: copySelection } =
    useCopyToClipboard(buildSelectionTsv);

  useEffect(() => {
    onSelectionChange?.(bounds !== null, buildSelectionTsv);
  }, [bounds, buildSelectionTsv, onSelectionChange]);

  // Ctrl/⌘+C copies the active selection, Escape clears it — both only
  // wired up while a selection exists. Copy is additionally skipped when
  // focus is on an editable element elsewhere on the page, so it doesn't
  // hijack a normal copy the user is making outside this grid; Escape has
  // no such risk, so it's not guarded the same way.
  useEffect(() => {
    if (!bounds) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelection(null);
        return;
      }
      const isCopyShortcut =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c';
      if (!isCopyShortcut) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      copySelection();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [bounds, copySelection]);

  // Clicking anywhere outside the grid also clears the selection — without
  // this, there was no way back to a true "nothing selected" state once a
  // user had clicked any cell. Listens on 'click' rather than 'mousedown':
  // a control outside the grid (e.g. the "Copy selection" button) that
  // reads the current selection on its own onClick needs that selection to
  // still be there when its handler runs — 'mousedown' fires, and would
  // clear it, before that handler does.
  useEffect(() => {
    if (!bounds) return;
    const handleDocumentClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setSelection(null);
      }
    };
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [bounds]);

  if (!hasData) {
    return <WorkspaceEmptyState message={CASHFLOW_NO_DATA_MESSAGE} />;
  }

  /** A plain value/label cell. */
  const renderValueCell = (
    rowIndex: number,
    colIndex: number,
    cell: GridCell,
    baseClass: string,
    negativeIsDanger?: boolean
  ) => {
    const display = cell.display ?? cell.text;
    const isNegative =
      negativeIsDanger &&
      cell.numericValue !== undefined &&
      cell.numericValue < 0;
    const content = cell.tooltip ? (
      <Tooltip title={cell.tooltip}>
        <span className="cursor-help font-normal text-gray-muted">{display}</span>
      </Tooltip>
    ) : (
      <span className={isNegative ? 'text-danger' : undefined}>{display}</span>
    );
    return (
      <td
        key={colIndex}
        onMouseDown={handleCellMouseDown(rowIndex, colIndex)}
        onMouseEnter={handleCellMouseEnter(rowIndex, colIndex)}
        className={`${baseClass} cursor-cell select-none ${getSelectionShadowClass(rowIndex, colIndex)}`}
      >
        {content}
      </td>
    );
  };

  /** The sticky first-column cell of a data (non-footer) row. */
  const renderLabelCell = (
    rowIndex: number,
    label: string,
    options: { bold?: boolean; indent?: boolean; rowBg?: string } = {}
  ) => (
    <td
      onMouseDown={handleCellMouseDown(rowIndex, 0)}
      onMouseEnter={handleCellMouseEnter(rowIndex, 0)}
      className={`max-w-[16rem] py-1.5 pl-2 pr-6 text-base cursor-cell select-none ${
        options.bold ? 'font-semibold' : ''
      } text-charcoal sticky left-0 ${options.rowBg ?? 'bg-white'} group-hover:bg-rowHover ${ROW_DIVIDER} ${stickyEdgeClass} ${getSelectionShadowClass(rowIndex, 0)}`}
    >
      <span className={options.indent ? 'block pl-8' : ''}>{label}</span>
    </td>
  );

  /** `idx` alternates the row's stripe within its own section (Income /
   * Expenditure); the sticky label cell must be given the same stripe
   * explicitly since a sticky cell's background can't just inherit its
   * <tr>'s. */
  const renderLineRow = (row: LineRowModel, idx = 0) => {
    const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
    return (
      <tr key={row.key} className={`group hover:bg-rowHover ${rowBg}`}>
        {renderLabelCell(row.rowIndex, row.cells[0].text, {
          bold: row.bold,
          indent: row.indent,
          rowBg,
        })}
        {row.cells
          .slice(1)
          .map((cell, i) =>
            renderValueCell(
              row.rowIndex,
              i + 1,
              cell,
              bodyCellClass,
              row.negativeIsDanger
            )
          )}
      </tr>
    );
  };

  // A single <td colSpan={N}> spanning the whole row is unreliable with
  // position: sticky across browsers (unlike the per-column sticky cells
  // used everywhere else in this grid) — a spanning cell's sticky offset
  // doesn't clamp against the scroll container the same way a normal-width
  // cell's does, so it scrolled out of view instead of pinning. This row
  // instead mirrors renderLineRow's own per-column structure (one real cell
  // per column) with the label only in the first, already-proven-sticky
  // column, and blank cells filling the rest. It's also deliberately not a
  // hover-highlighted `group` row and has no divider border, matching the
  // original section-label styling exactly.
  const renderSectionLabelRow = (
    key: string,
    rowIndex: number,
    label: string
  ) => (
    <tr key={key}>
      <td
        onMouseDown={handleCellMouseDown(rowIndex, 0)}
        onMouseEnter={handleCellMouseEnter(rowIndex, 0)}
        className={`max-w-[16rem] ${sectionLabelClass} sticky left-0 cursor-cell select-none bg-white ${stickyEdgeClass} ${getSelectionShadowClass(rowIndex, 0)}`}
      >
        {label}
      </td>
      {Array.from({ length: 2 + periods.length }, (_, i) => i + 1).map(
        (colIndex) => (
          <td
            key={colIndex}
            onMouseDown={handleCellMouseDown(rowIndex, colIndex)}
            onMouseEnter={handleCellMouseEnter(rowIndex, colIndex)}
            className={`cursor-cell select-none bg-white ${getSelectionShadowClass(rowIndex, colIndex)}`}
          />
        )
      )}
    </tr>
  );

  const renderFooterLabelCell = (
    rowIndex: number,
    label: string,
    accent?: boolean
  ) => (
    <td
      onMouseDown={handleCellMouseDown(rowIndex, 0)}
      onMouseEnter={handleCellMouseEnter(rowIndex, 0)}
      className={`max-w-[16rem] py-1.5 pl-2 pr-6 text-base font-bold text-charcoal cursor-cell select-none ${FOOTER_BASE} z-20 left-0 ${accent ? 'bg-gray-bright' : ''} ${stickyEdgeClass} ${getSelectionShadowClass(rowIndex, 0)}`}
    >
      {label}
    </td>
  );

  const renderFooterValueCell = (
    rowIndex: number,
    colIndex: number,
    cell: GridCell,
    accent?: boolean
  ) => {
    const display = cell.display ?? cell.text;
    const content = cell.tooltip ? (
      <Tooltip title={cell.tooltip}>
        <span className="cursor-help font-normal text-gray-muted">{display}</span>
      </Tooltip>
    ) : (
      <span
        className={
          cell.numericValue !== undefined && cell.numericValue < 0
            ? 'text-danger'
            : undefined
        }
      >
        {display}
      </span>
    );
    return (
      <td
        key={colIndex}
        onMouseDown={handleCellMouseDown(rowIndex, colIndex)}
        onMouseEnter={handleCellMouseEnter(rowIndex, colIndex)}
        className={`${FOOTER_BASE} w-28 px-2 text-right cursor-cell select-none ${accent ? 'bg-gray-bright' : ''} ${getSelectionShadowClass(rowIndex, colIndex)}`}
      >
        {content}
      </td>
    );
  };

  return (
    <div ref={rootRef} className="flex min-h-0 flex-1 flex-col gap-2">
      {/* Always rendered at a fixed height (visibility toggled, not
          presence) so appearing/disappearing never shifts the grid below
          it — conditionally mounting this bar caused the whole grid to
          jump on every selection change. */}
      <div
        className={`flex h-9 flex-wrap items-center gap-3 rounded-md border border-gray-smoke bg-success-light px-3 py-1.5 text-sm text-gray-dark ${
          selectionStats ? '' : 'invisible'
        }`}
      >
        {selectionStats && (
          <>
            <span>
              <span className="font-semibold text-gray-muted">Sum:</span>{' '}
              <span className="font-bold text-success-dark">
                {formatCurrency(selectionStats.sum)}
              </span>
            </span>
            <span className="h-4 border-l border-gray-argent" />
            <span>
              <span className="font-semibold text-gray-muted">Average:</span>{' '}
              <span className="font-bold text-success-dark">
                {formatCurrency(selectionStats.average)}
              </span>
            </span>
            {/* Cmd/Ctrl+C is the only way to copy a selection now — this is
                just a transient confirmation that the shortcut worked, not
                a control of its own. */}
            {selectionCopied ? (
              <span className="ml-auto flex items-center gap-1 font-semibold text-success">
                <LuCheck size={14} />
                Copied
              </span>
            ) : (
              <span className="ml-auto inline-flex items-center gap-1 text-xs text-gray-muted">
                <ShortcutHint shortcutKey="C" /> to copy
              </span>
            )}
          </>
        )}
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="summary-grid-scroll w-0 min-w-full flex-1 overflow-x-auto"
      >
        <table className="w-max border-separate border-spacing-0 text-base">
          <thead>
            <tr>
              <th
                onMouseDown={handleCellMouseDown(model.headerRowIndex, 0)}
                onMouseEnter={handleCellMouseEnter(model.headerRowIndex, 0)}
                className={`${headerLabelClass} cursor-cell select-none ${getSelectionShadowClass(model.headerRowIndex, 0)}`}
              >
                Line item
              </th>
              <th
                onMouseDown={handleCellMouseDown(model.headerRowIndex, 1)}
                onMouseEnter={handleCellMouseEnter(model.headerRowIndex, 1)}
                className={`${headerCellClass} cursor-cell select-none ${getSelectionShadowClass(model.headerRowIndex, 1)}`}
              >
                Total
              </th>
              <th
                onMouseDown={handleCellMouseDown(model.headerRowIndex, 2)}
                onMouseEnter={handleCellMouseEnter(model.headerRowIndex, 2)}
                className={`${headerCellClass} cursor-cell select-none ${getSelectionShadowClass(model.headerRowIndex, 2)}`}
              >
                No Date
                <HeaderInfo tooltip={NO_DATE_TOOLTIP} />
              </th>
              {periods.map((p, i) => (
                <th
                  key={p.label + i}
                  onMouseDown={handleCellMouseDown(model.headerRowIndex, 3 + i)}
                  onMouseEnter={handleCellMouseEnter(
                    model.headerRowIndex,
                    3 + i
                  )}
                  className={`${headerCellClass} cursor-cell select-none ${getSelectionShadowClass(model.headerRowIndex, 3 + i)}`}
                >
                  {p.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderSectionLabelRow(
              'income-label',
              model.incomeLabelRowIndex,
              'Income'
            )}
            {showProducts &&
              model.incomeLines.map((line, idx) => renderLineRow(line, idx))}
            {renderLineRow(model.totalIncome)}

            {renderSectionLabelRow(
              'expenditure-label',
              model.expenditureLabelRowIndex,
              'Expenditure'
            )}
            {model.expenditureLines.map((line, idx) =>
              renderLineRow(line, idx)
            )}
            {renderLineRow(model.totalExpenditure)}
          </tbody>
          <tfoot>
            <tr>
              {renderFooterLabelCell(
                model.balance.rowIndex,
                model.balance.cells[0].text
              )}
              {model.balance.cells
                .slice(1)
                .map((cell, i) =>
                  renderFooterValueCell(model.balance.rowIndex, i + 1, cell)
                )}
            </tr>
            <tr>
              {renderFooterLabelCell(
                model.cashFlow.rowIndex,
                model.cashFlow.cells[0].text,
                true
              )}
              {model.cashFlow.cells
                .slice(1)
                .map((cell, i) =>
                  renderFooterValueCell(
                    model.cashFlow.rowIndex,
                    i + 1,
                    cell,
                    true
                  )
                )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default CashflowGrid;
